// 図形をドラッグで配置している最中のプレビュー（ゴースト）枠。
// ステージ座標で配置するため、親のステージ（pan/zoom transform 配下）に置くことで
// pan/zoom に追従して正しい位置・サイズで表示される。確定済みの図形と同じ ShapeGlyph を
// 使って描画するため、ドロップ後の最終的な見た目と一致する。枠自体はヒットを取らない
// （pointerEvents: none）ので、ドラッグ中のポインタ処理を邪魔しない。

import { useCanvasStore } from "./store/canvas-store";
import { ShapeGlyph } from "./objects/shape-object";
import { TableGrid } from "./objects/table-object";
import { TABLE_DEFAULT_COLUMNS, TABLE_DEFAULT_ROWS } from "./object-placement-defaults";
import { SHAPE_FILL_NONE, SHAPE_STROKE_WIDTH_PIXELS } from "../../lib/shape-preference";

export function CanvasShapePlacementOverlay(): JSX.Element | null {
  const draft = useCanvasStore((s) => s.shapePlacementDraft);
  const tool = useCanvasStore((s) => s.tool);
  const shapeKind = useCanvasStore((s) => s.shapeKind);
  const shapeFill = useCanvasStore((s) => s.shapeFill);
  const shapeStrokePattern = useCanvasStore((s) => s.shapeStrokePattern);
  const shapeStrokeWidth = useCanvasStore((s) => s.shapeStrokeWidth);
  const sectionFill = useCanvasStore((s) => s.sectionFill);
  if (!draft) return null;
  // セクションのドラッグ配置中は、確定後のセクションと同じ見た目（角丸 + 半透明の塗り）で
  // プレビューする（ジェスチャは図形と共有だが、描画は種別に合わせて分ける）。
  if (tool === "place-section") {
    return (
      <div
        data-testid="canvas-section-placement-preview"
        style={{
          position: "absolute",
          left: `${draft.x}px`,
          top: `${draft.y}px`,
          width: `${draft.w}px`,
          height: `${draft.h}px`,
          pointerEvents: "none",
          zIndex: Number.MAX_SAFE_INTEGER,
          borderRadius: "8px",
          background: `color-mix(in srgb, ${sectionFill} 16%, transparent)`,
          boxShadow: `inset 0 0 0 calc(2px * var(--canvas-inv-zoom, 1)) var(--color-accent)`,
        }}
      />
    );
  }
  if (tool === "place-table") {
    const cells = Array.from({ length: TABLE_DEFAULT_ROWS }, () =>
      new Array<string>(TABLE_DEFAULT_COLUMNS).fill("")
    );
    return (
      <div
        data-testid="canvas-table-placement-preview"
        style={{
          position: "absolute",
          left: `${draft.x}px`,
          top: `${draft.y}px`,
          width: `${draft.w}px`,
          height: `${draft.h}px`,
          pointerEvents: "none",
          zIndex: Number.MAX_SAFE_INTEGER,
        }}
      >
        <TableGrid cells={cells} width={draft.w} height={draft.h} fontSize={16} />
      </div>
    );
  }
  // 背景なしのときは transparent で描画する（確定オブジェクトの fill 未設定と同じ見た目）。
  const fill = shapeFill === SHAPE_FILL_NONE ? "transparent" : shapeFill;
  return (
    <svg
      data-testid="canvas-shape-placement-preview"
      style={{
        position: "absolute",
        left: `${draft.x}px`,
        top: `${draft.y}px`,
        width: `${draft.w}px`,
        height: `${draft.h}px`,
        pointerEvents: "none",
        overflow: "visible",
        zIndex: Number.MAX_SAFE_INTEGER,
      }}
      viewBox={`0 0 ${draft.w} ${draft.h}`}
      preserveAspectRatio="none"
    >
      <ShapeGlyph
        shapeKind={shapeKind}
        w={draft.w}
        h={draft.h}
        fill={fill}
        stroke="var(--color-accent)"
        strokePattern={shapeStrokePattern}
        strokeWidth={SHAPE_STROKE_WIDTH_PIXELS[shapeStrokeWidth]}
        arrowFromEdge={draft.arrowFromEdge}
        arrowFromCorner={draft.arrowFromCorner}
        pointerEvents="none"
      />
    </svg>
  );
}
